import { useState, useEffect, useCallback } from "react";
import axios from "axios";

const EMPTY_SUMMARY = {
  overdue: 0,
  dueSoon: 0,
  ideasAttention: 0,
  ideasDraftStale: 0,
  personalIncomplete: 0,
  projectsAttention: 0,
  abandonedCarts: 0,
  total: 0,
};

function applyPayload(payload) {
  const summary = { ...EMPTY_SUMMARY, ...(payload?.summary || {}) };
  const items = Array.isArray(payload?.items) ? payload.items : [];
  if (!summary.total && items.length) {
    summary.total = items.length;
  }
  return { ...summary, items, loading: false, error: null };
}

/**
 * CRM bildirimleri — navbar, dashboard (GET /api/notifications).
 */
export function useCrmInsights(options = {}) {
  const { pollMs = 120000 } = options;
  const [data, setData] = useState({
    ...EMPTY_SUMMARY,
    items: [],
    loading: true,
    error: null,
    marking: false,
  });

  const refresh = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      setData({
        ...EMPTY_SUMMARY,
        items: [],
        loading: false,
        error: null,
        marking: false,
      });
      return;
    }

    setData((d) => ({ ...d, loading: true, error: null }));
    try {
      const { data: payload } = await axios.get("/api/notifications");
      setData({ ...applyPayload(payload), marking: false });
    } catch (err) {
      setData({
        ...EMPTY_SUMMARY,
        items: [],
        loading: false,
        error: err?.response?.data?.message || "Bildirimler yüklenemedi",
        marking: false,
      });
    }
  }, []);

  const markAsRead = useCallback(async (notificationId) => {
    if (!notificationId) return;
    setData((d) => ({ ...d, marking: true }));
    try {
      const { data: payload } = await axios.post(
        `/api/notifications/${encodeURIComponent(notificationId)}/read`
      );
      setData({ ...applyPayload(payload), marking: false });
    } catch (err) {
      setData((d) => ({
        ...d,
        marking: false,
        error: err?.response?.data?.message || "Okundu işaretlenemedi",
      }));
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    setData((d) => ({ ...d, marking: true }));
    try {
      const { data: payload } = await axios.post("/api/notifications/read-all");
      setData({ ...applyPayload(payload), marking: false });
    } catch (err) {
      setData((d) => ({
        ...d,
        marking: false,
        error: err?.response?.data?.message || "Bildirimler okundu işaretlenemedi",
      }));
    }
  }, []);

  useEffect(() => {
    refresh();
    if (pollMs <= 0) return undefined;
    const id = setInterval(refresh, pollMs);
    return () => clearInterval(id);
  }, [refresh, pollMs]);

  useEffect(() => {
    const onRefresh = () => refresh();
    window.addEventListener("crmInsightsRefresh", onRefresh);
    return () => window.removeEventListener("crmInsightsRefresh", onRefresh);
  }, [refresh]);

  return {
    overdue: data.overdue,
    dueSoon: data.dueSoon,
    ideasAttention: data.ideasAttention,
    ideasDraftStale: data.ideasDraftStale,
    personalIncomplete: data.personalIncomplete,
    projectsAttention: data.projectsAttention,
    abandonedCarts: data.abandonedCarts,
    total: data.total,
    items: data.items,
    loading: data.loading,
    error: data.error,
    marking: data.marking,
    refresh,
    markAsRead,
    markAllAsRead,
  };
}

export function bumpCrmInsights() {
  window.dispatchEvent(new Event("crmInsightsRefresh"));
}
